import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { api, ChatChannel, ChatEvent, ChatUnreadInfo, NotificationView } from '../api';
import { useApp } from '../App';
import { forgetAll } from './deviceMemory';
import { applyEvent, isReadChatTag, previewOf, withUnread } from './channels';
import { mentionsLogin } from '../lib/mentions';
import { notificationPreview } from './notificationView';
import {
  cachedEndpoint,
  closeShown,
  currentEndpoint,
  isStandalone,
  notifyLocally,
  PUSH_CHANGED,
  registerServiceWorker,
} from './notifications';
import { playSound } from '../lib/sounds';
import { claimOnce } from '../lib/tabs';
import { useOutbox, type EnqueueInput } from './useOutbox';
import { indexedDbQueueStore, type QueueStore } from './deviceStore';
import type { Draft } from './outbox';

/*
 * Единственное соединение с потоком событий на всё приложение.
 *
 * Оно живёт, пока человек в системе, а не только пока открыт раздел «Чат»:
 * иначе не было бы ни счётчика непрочитанного в меню, ни уведомлений на
 * рабочем столе во время работы с гантограммой. Сервер по этому же
 * соединению понимает, что человек на связи, и не шлёт ему веб-пуш.
 */

/*
 * Чат уведомлений — не запись в базе, а тот же список уведомлений, показанный
 * лентой (решение Ильи 10.09.2026). Отдельного хранилища не заводим: иначе
 * колокольчик и чат разошлись бы, и «прочитано» пришлось бы сшивать с двух
 * сторон. Строка в списке чатов собирается здесь и всегда стоит первой.
 */
export const NOTIFY_CHANNEL_ID = 'notifications';

/** Поток событий (пере)открылся: события за время разрыва могли пропасть. */
export const STREAM_OPENED = 'code-stream-opened';

type ChatCtx = {
  channels: ChatChannel[];
  /** Первая загрузка списка: показываем заглушки, а не пустоту. */
  loading: boolean;
  /** Список не загрузился — причина для человека. */
  error: string | null;
  unreadTotal: number;
  setActive: (id: string | null) => void;
  reload: () => Promise<void>;
  /** Подписка на поток; возвращает отписку. */
  subscribe: (fn: (e: ChatEvent) => void) => () => void;
  /** Канал, который просили открыть (клик по уведомлению). */
  pendingOpen: string | null;
  requestOpen: (channelId: string) => void;
  clearPendingOpen: () => void;
  markLocallyRead: (channelId: string) => void;
  /** Поставить непрочитанное чата значением с сервера: число, отсечку, ветку, упоминания. */
  setUnread: (channelId: string, info: ChatUnreadInfo) => void;
  /** Уведомления: их показывает и колокольчик, и чат уведомлений. */
  notifications: NotificationView[];
  notifUnread: number;
  /** Счётчик уже настоящий, а не начальный ноль: по нему звенит колокольчик. */
  notifLoaded: boolean;
  /** Есть ли что грузить дальше в ленте уведомлений. */
  notifMore: boolean;
  loadNotifications: (limit?: number) => Promise<void>;
  markNotificationsRead: (payload: { id?: number; all?: boolean; upTo?: number }) => Promise<number>;
  /** Кто печатает в каждом чате прямо сейчас: имя, чтобы показать в списке. */
  typingIn: Record<string, string>;
  /*
   * Голосовые, которые человек уже слушал. Раньше это знал только сам
   * проигрыватель и забывал при уходе из раздела; теперь отметка живёт на
   * сервере, а здесь лежит её копия на время сеанса.
   */
  voicePlayed: ReadonlySet<number>;
  /** Прочитать отметки переписки — вызывается при её открытии. */
  loadVoicePlayed: (channelId: string) => void;
  /** Голосовое запустили: гасим точку сразу и сообщаем серверу. */
  markVoicePlayed: (fileId: number) => void;
  /** Отправленное, но ещё не доехавшее: показывается в ленте черновиком. */
  drafts: Draft[];
  enqueueDraft: (input: EnqueueInput) => void;
  retryDraft: (key: string) => void;
  dropDraft: (key: string) => void;
  /** Номера сообщений, доставленных очередью: черновик уже показал их появление сам. */
  settledIds: ReadonlySet<number>;
  /** Сбросить набор доставленных: та же смена переписки, что чистит новые строки в ленте. */
  clearSettled: () => void;
};

const Ctx = createContext<ChatCtx | null>(null);
export const useChat = () => useContext(Ctx)!;

/* Одно хранилище на вкладку: открытое соединение с IndexedDB переиспользуется. */
const deviceQueue = indexedDbQueueStore();

/**
 * Явный выход из учётной записи: с устройства уходит всё, что чат хранил для
 * этого человека. На телефоне могут сменить пользователя. Истёкшая сессия и
 * выход по бездействию не чистят — вернётся тот же человек и не должен
 * потерять неотправленное. Тексты поля ввода и места чтения стирает forgetAll
 * и помечает человека забытым: запоздавшая запись закрывающегося экрана
 * стёртое не вернёт.
 */
export async function forgetChatDevice(userId: string): Promise<void> {
  forgetAll(userId);
  await deviceQueue.clear(userId).catch(() => {});
}

export function ChatProvider({
  children,
  queueStore = deviceQueue,
}: {
  children: React.ReactNode;
  /** Только для тестов: подмена хранилища очереди. */
  queueStore?: QueueStore;
}) {
  const app = useApp();
  const myId = app.user.id;
  const myLogin = app.user.login;
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingOpen, setPendingOpen] = useState<string | null>(null);
  /** Кто печатает в каждом чате: имя и момент последнего сигнала. */
  const [typists, setTypists] = useState<Record<string, { name: string; at: number }>>({});
  /** Уведомления: их показывает и колокольчик, и чат уведомлений. */
  const [notifications, setNotifications] = useState<NotificationView[]>([]);
  const [notifUnread, setNotifUnread] = useState(0);
  /*
   * Список уведомлений уже прочитан с сервера. До этого счётчик равен нулю не
   * потому, что непрочитанного нет, а потому, что его ещё не спрашивали, — и
   * колокольчик принимал первый настоящий ответ за прибавку и звенел на
   * каждое открытие приложения (жалоба Ильи 21.09.2026).
   */
  const [notifLoaded, setNotifLoaded] = useState(false);
  const [notifFirstUnread, setNotifFirstUnread] = useState<number | null>(null);
  const [notifMore, setNotifMore] = useState(false);
  const [voicePlayed, setVoicePlayed] = useState<ReadonlySet<number>>(() => new Set<number>());
  const outbox = useOutbox(myId, queueStore);
  const listeners = useRef(new Set<(e: ChatEvent) => void>());
  /** Канал, открытый на экране: в нём сообщения сразу считаются прочитанными. */
  const activeRef = useRef<string | null>(null);
  const channelsRef = useRef<ChatChannel[]>([]);
  channelsRef.current = channels;

  const reload = useCallback(async () => {
    try {
      const list = await api.chatChannels();
      /*
       * Список ставится сразу, а не в очередь: звавшие `reload().then(...)`
       * открывают чат, и стопка экранов перерисовывается синхронно. Со
       * списком в очереди кадр с новой стопкой видел старый список, не
       * находил в нём открываемый чат и сбрасывал стопку — новый диалог
       * (Педро, любой первый разговор) не открывался в правой панели.
       */
      flushSync(() => {
        setChannels(list);
        setError(null);
      });
    } catch (e) {
      // молчаливый catch здесь стоил нам вечера: пустой список выглядел
      // как «чатов нет», хотя сервер просто не ответил
      setError(e instanceof Error ? e.message : 'Не удалось загрузить чаты');
    } finally {
      setLoading(false);
    }
  }, []);

  /*
   * Перечитать список, но не чаще раза в треть секунды. Событие о чате
   * приходит пачками — позвали пятерых, сменили роли, — и на каждое ходить
   * за списком незачем: список собирается целиком, одного раза хватает.
   */
  const reloadTimer = useRef<number | null>(null);
  const reloadSoon = useCallback(() => {
    if (reloadTimer.current !== null) return;
    reloadTimer.current = window.setTimeout(() => {
      reloadTimer.current = null;
      void reload();
    }, 300);
  }, [reload]);

  /*
   * Сколько уведомлений просили самое большее. Лента раскрывается до
   * шестидесяти кнопкой «Показать более ранние»; любое перечитывание —
   * событие, минутный опрос, открытие колокольчика — берёт не меньше
   * этого, иначе раскрытая лента теряла бы старые карточки при каждом
   * прочтении.
   */
  const notifLimit = useRef(30);
  const loadNotifications = useCallback(async (limit = 30) => {
    notifLimit.current = Math.max(notifLimit.current, limit);
    try {
      const res = await api.notifications({ limit: notifLimit.current });
      setNotifications(res.items);
      setNotifUnread(res.unread);
      setNotifFirstUnread(res.firstUnreadId);
      setNotifMore(res.more);
      setNotifLoaded(true);
    } catch {
      /* колокольчик и чат уведомлений просто останутся с прежними данными */
    }
  }, []);

  /** Отметить прочитанным одно уведомление, всё сразу или всё до указанного. */
  const markNotificationsRead = useCallback(
    async (payload: { id?: number; all?: boolean; upTo?: number }) => {
      const res = await api.notifRead(payload);
      setNotifUnread(res.unread);
      setNotifications((prev) =>
        prev.map((n) =>
          payload.all ||
          (payload.id && n.id === payload.id) ||
          (payload.upTo && n.id <= payload.upTo)
            ? { ...n, read: true }
            : n,
        ),
      );
      return res.unread;
    },
    [],
  );

  /*
   * Событие `notifications` приходит пачкой: колокольчик отмечает прочитанными
   * несколько карточек — по событию на каждую, на каждой вкладке. Список
   * перечитывается целиком, поэтому на пачку хватает одного запроса.
   */
  const notifTimer = useRef<number | null>(null);
  const loadNotificationsSoon = useCallback(() => {
    if (notifTimer.current !== null) return;
    notifTimer.current = window.setTimeout(() => {
      notifTimer.current = null;
      void loadNotifications();
    }, 300);
  }, [loadNotifications]);

  const setActive = useCallback((id: string | null) => {
    activeRef.current = id;
  }, []);

  const markLocallyRead = useCallback((channelId: string) => {
    setChannels((prev) => prev.map((c) => (c.id === channelId ? { ...c, unread: 0 } : c)));
  }, []);

  /** Непрочитанное чата, каким его посчитал сервер после отметки: то же правило, что у события `unread`. */
  const setUnread = useCallback((channelId: string, info: ChatUnreadInfo) => {
    setChannels((prev) => withUnread(prev, channelId, info));
  }, []);

  const subscribe = useCallback((fn: (e: ChatEvent) => void) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  const requestOpen = useCallback((channelId: string) => setPendingOpen(channelId), []);
  const clearPendingOpen = useCallback(() => setPendingOpen(null), []);

  /**
   * Событие потока правит список чатов: новое сообщение, прочтение,
   * присутствие собеседника. Сама правка — в channels.ts; здесь остаётся
   * то, что чистой функцией не выразить: звук и уведомление.
   */
  const onEvent = useCallback(
    (e: ChatEvent) => {
      // колокольчик: новое уведомление или прочитанное на другом устройстве
      if (e.type === 'notifications') {
        loadNotificationsSoon();
        return;
      }

      if (e.type === 'message' && !channelsRef.current.some((c) => c.id === e.channelId)) {
        // канал появился только что (позвали в новый) — перечитываем список
        void reload();
        return;
      }

      /*
       * Событие о самом чате: переименовали, сменили картинку, позвали или
       * выгнали, поменяли роль. Собрать это правкой на месте нельзя — у
       * клиента нет ни состава, ни прав, — поэтому перечитываем список
       * целиком. Раньше событие не слушал никто, и всё перечисленное
       * доезжало только с первым сообщением или по F5.
       */
      if (e.type === 'channel') {
        reloadSoon();
        return;
      }

      /*
       * Удалили последнее сообщение — в строке под названием чата должно
       * встать предыдущее, а его у клиента нет. Перечитываем список: сервер
       * сам найдёт новое последнее. Удаление любого другого сообщения строку
       * не трогает.
       */
      if (e.type === 'message.deleted') {
        const c = channelsRef.current.find((x) => x.id === e.channelId);
        if (c?.last && c.last.id === e.data.id) reloadSoon();
      }

      /*
       * «Печатает…» показывается и в списке слева, а не только в открытой
       * переписке: строка сама гаснет через несколько секунд, если человек
       * перестал набирать.
       */
      if (e.type === 'typing') {
        const who = e.data.user;
        if (who.id !== myId) {
          setTypists((prev) => ({ ...prev, [e.channelId]: { name: who.name, at: Date.now() } }));
        }
      }

      const seenNow = (channelId: string) =>
        activeRef.current === channelId && document.hasFocus();
      setChannels((prev) => applyEvent(prev, e, { myId, myLogin, seenNow }));

      /*
       * Новый ответ в моей ветке. Сервер шлёт `unread` только участникам
       * ветки, поэтому звеним по нему, а не по самому ответу: число выросло,
       * виновата ветка, вкладка не в фокусе (в фокусе ответ и так виден в
       * списке). Выросли упоминания — о них уже сказало само сообщение.
       * channelsRef ещё держит список до события — с ним и сравниваем.
       */
      if (e.type === 'unread') {
        const was = channelsRef.current.find((c) => c.id === e.channelId);
        const info = e.data;
        if (!was || was.muted || !info.threadRootId || info.unread <= was.unread) return;
        if (info.mentions > was.mentions || document.hasFocus()) return;
        if (!claimOnce(`thread:${e.channelId}:${info.threadRootId}:${info.unread}`)) return;
        playSound('message');
        notifyLocally(was.name, 'Новый ответ в ветке', e.channelId);
        return;
      }

      if (e.type !== 'message') return;
      const msg = e.data;
      const channel = channelsRef.current.find((c) => c.id === e.channelId);
      if (!channel || msg.author.id === myId) return;
      // ответ в ветке звенит событием `unread` выше — только своим участникам;
      // здесь — лишь если в нём обращаются ко мне
      const mentioned = mentionsLogin(msg.text, myLogin);
      // беззвучный чат молчит, кроме обращения ко мне: сервер пропускает такой пуш
      if (channel.muted && !mentioned) return;
      if (msg.parentId && !mentioned) return;
      // звук и уведомление — одной вкладкой на весь браузер, а не каждой
      if (!claimOnce(`message:${msg.id}`)) return;
      playSound('message');
      const title =
        channel.kind === 'dm' ? msg.author.name : `${msg.author.name} · ${channel.name}`;
      const body = previewOf(msg) || 'Новое сообщение';
      // обращение ко мне видно с первых слов — так же начинается и пуш с сервера
      notifyLocally(title, mentioned ? `Вас упомянули: ${body}` : body, e.channelId);
    },
    [myId, myLogin, reload, reloadSoon, loadNotificationsSoon],
  );

  /*
   * Колокольчик живёт событием `notifications` (спека
   * 2026-10-02-tabs-devices-sync, раздел 3). Опрос раз в минуту остаётся
   * страховкой: поток событий открыт только при доступе к чату, а
   * колокольчик есть у всех.
   */
  useEffect(() => {
    void loadNotifications();
    const id = window.setInterval(() => {
      if (document.hasFocus()) void loadNotifications();
    }, 60_000);
    return () => {
      window.clearInterval(id);
      if (notifTimer.current !== null) window.clearTimeout(notifTimer.current);
      notifTimer.current = null;
    };
  }, [loadNotifications]);

  /*
   * Прочитанный чат — его системные уведомления больше не нужны: закрываем
   * их, где бы чат ни прочитали. Вкладка может быть свёрнутой — поток событий
   * у неё живой, и уведомление на рабочем столе должно уйти, не дожидаясь,
   * пока её откроют. На телефоне замороженная страница эффектов не выполняет:
   * там это срабатывает, когда приложение открыли — пуш на каждое прочтение
   * не шлём (решение Ильи 02.10.2026). Список сверяется в момент закрытия, а
   * не при запуске: сообщение, пришедшее за это время, свежее уведомление не
   * теряет.
   */
  useEffect(() => {
    if (!channels.some((c) => c.unread === 0)) return;
    void closeShown((tag) => isReadChatTag(tag, channelsRef.current));
  }, [channels]);

  useEffect(() => {
    void reload();

    let offSw = () => {};
    if ('serviceWorker' in navigator) {
      // регистрация нужна и без подписки: по клику на пуш она будит вкладку
      registerServiceWorker().catch(() => {});
      const onSwMessage = (e: MessageEvent) => {
        if (e.data?.type === 'open-chat' && e.data.channelId) setPendingOpen(e.data.channelId);
      };
      navigator.serviceWorker.addEventListener('message', onSwMessage);
      offSw = () => navigator.serviceWorker.removeEventListener('message', onSwMessage);
    }

    let opened = false;
    let source: EventSource | null = null;
    /* Когда поток последний раз подавал признаки жизни (событие или ping). */
    let lastBeat = Date.now();
    let stopped = false;
    /* С каким адресом подписки открыт текущий поток. */
    let streamEndpoint = '';

    const open = (endpoint: string) => {
      source?.close();
      streamEndpoint = endpoint;
      const url = endpoint
        ? `/api/chat/stream?endpoint=${encodeURIComponent(endpoint)}`
        : '/api/chat/stream';
      const es = new EventSource(url, { withCredentials: true });
      source = es;
      lastBeat = Date.now();
      es.onopen = () => {
        lastBeat = Date.now();
        // после обрыва связи список мог устареть — перечитываем
        if (opened) void reload();
        opened = true;
        // пока потока не было, мог прийти звонок — кому надо, перепроверит
        window.dispatchEvent(new Event(STREAM_OPENED));
      };
      es.onmessage = (ev) => {
        lastBeat = Date.now();
        let event: ChatEvent;
        try {
          event = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (event.type === 'ping') return;
        onEvent(event);
        listeners.current.forEach((fn) => fn(event));
      };
    };

    /*
     * Адрес пуш-подписки этого устройства: по нему сервер не шлёт пуш сюда,
     * а на остальные устройства человека — шлёт. Поток открываем сразу с
     * запомненным адресом, а настоящий спрашиваем у системы в фоне: на
     * iPhone этот ответ после возврата из фона приходил по 15 секунд.
     */
    const connect = () => {
      open(cachedEndpoint());
      void currentEndpoint().then((endpoint) => {
        if (stopped || !source || endpoint === streamEndpoint) return;
        open(endpoint);
      });
    };
    connect();
    const onPushChanged = () => connect();
    window.addEventListener(PUSH_CHANGED, onPushChanged);

    /*
     * Телефон: приложение ушло в фон — поток закрываем сами.
     *
     * iOS замораживает страницу, но соединение с сервером держит ещё долго:
     * сервер видит «устройство на связи» и пуш на него не шлёт, хотя показать
     * сообщение уже некому. Закрытый поток — честный сигнал «меня нет», и
     * следующее сообщение придёт пушем. При возвращении поток открываем
     * заново, а список перечитывает onopen. На компьютере вкладка в фоне
     * остаётся на связи: там уведомление рисует сама страница.
     */
    const phone = isStandalone() || window.matchMedia('(pointer: coarse)').matches;
    const onVisibility = () => {
      if (!phone) return;
      if (document.visibilityState === 'hidden') {
        source?.close();
        source = null;
      } else if (!source) {
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    /*
     * Сторож живости потока.
     *
     * Сервер шлёт ping каждые 25 секунд. Молчание дольше минуты означает, что
     * поток умер незаметно: соединение оборвали посередине (уснул ноутбук,
     * сменилась сеть, перезапустился прокси), а браузер этого не понял и
     * переподключаться не стал. Вкладка при этом выглядит рабочей, но событий
     * больше не получает, и данные на экране тихо устаревают. Поэтому
     * пересоздаём поток сами и заодно перечитываем список чатов.
     */
    const watch = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - lastBeat < 70_000) return;
      connect();
      void reload();
    }, 15_000);

    return () => {
      stopped = true;
      clearInterval(watch);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener(PUSH_CHANGED, onPushChanged);
      source?.close();
      offSw();
    };
  }, [reload, onEvent]);

  /*
   * Печатающие живут четыре секунды: сигнал приходит раз в пару секунд, пока
   * человек набирает, и молчит, когда он остановился. Тикаем раз в секунду —
   * этого хватает, чтобы строка гасла вовремя, и не будит перерисовку зря.
   */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!Object.keys(typists).length) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [typists]);

  const typingIn = useMemo(() => {
    const fresh: Record<string, string> = {};
    for (const [channelId, t] of Object.entries(typists)) {
      if (now - t.at < 4000) fresh[channelId] = t.name;
    }
    return fresh;
  }, [typists, now]);

  /*
   * Строка чата уведомлений: собирается из последнего уведомления и счётчика
   * непрочитанных. Всегда первая в списке — её место не зависит от времени.
   */
  const withNotifications = useMemo<ChatChannel[]>(() => {
    const last = notifications[0];
    const row: ChatChannel = {
      id: NOTIFY_CHANNEL_ID,
      kind: 'system',
      name: 'Уведомления',
      topic: '',
      isPrivate: false,
      projectId: null,
      archived: false,
      peer: null,
      peerOnline: false, peerActive: true,
      unread: notifUnread,
      firstUnreadId: notifFirstUnread,
      // у уведомлений нет ни веток, ни упоминаний
      threadRootId: null,
      mentions: 0,
      mention: null,
      pinned: null,
      muted: false,
      role: 'member',
      canPost: false,
      avatarAt: null,
      lastMessageAt: last?.ts ?? 0,
      hidden: false,
      folderId: null,
      last: last
        ? {
            id: last.id,
            text: notificationPreview(last),
            authorName: '',
            mine: false,
            ts: last.ts,
            seenByAll: true,
          }
        : null,
    };
    return [row, ...channels];
  }, [channels, notifications, notifUnread, notifFirstUnread]);

  /*
   * Отметки о прослушанных голосовых этой переписки. Ходим за ними при
   * открытии чата: в ленту сообщений они не встроены — та собирается без
   * человека внутри (server/src/chat/voice-plays.service.ts).
   */
  const loadVoicePlayed = useCallback((channelId: string) => {
    api
      .chatVoicePlayed(channelId)
      .then((ids) => setVoicePlayed((prev) => new Set([...prev, ...ids])))
      // отметки — не содержимое переписки: не доехали, значит точка просто
      // останется гореть, ломать открытие чата из-за этого незачем
      .catch(() => {});
  }, []);

  const markVoicePlayed = useCallback((fileId: number) => {
    setVoicePlayed((prev) => (prev.has(fileId) ? prev : new Set([...prev, fileId])));
    api.chatMarkVoicePlayed(fileId).catch(() => {});
  }, []);

  const unreadTotal = useMemo(
    () => withNotifications.reduce((sum, c) => sum + (c.muted ? 0 : c.unread), 0),
    [withNotifications],
  );

  const value: ChatCtx = {
    channels: withNotifications,
    loading,
    error,
    unreadTotal,
    setActive,
    reload,
    subscribe,
    pendingOpen,
    requestOpen,
    clearPendingOpen,
    markLocallyRead,
    setUnread,
    typingIn,
    notifications,
    notifUnread,
    notifLoaded,
    notifMore,
    loadNotifications,
    markNotificationsRead,
    voicePlayed,
    loadVoicePlayed,
    markVoicePlayed,
    drafts: outbox.drafts,
    enqueueDraft: outbox.enqueueDraft,
    retryDraft: outbox.retryDraft,
    dropDraft: outbox.dropDraft,
    settledIds: outbox.settledIds,
    clearSettled: outbox.clearSettled,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
